/**
 * Distingue « un endpoint a hoqueté » de « l'API est tombée ».
 *
 * Sans cette distinction il n'existe que deux réglages, mauvais tous les deux :
 * tout remonter en `error` — le bruit qui a fait clore ce ticket une première
 * fois sans rien corriger — ou tout taire, et laisser une panne générale passer
 * inaperçue. Le critère retenu est la *dispersion* : un endpoint seul qui échoue
 * est un incident local ; plusieurs endpoints sans rapport qui échouent
 * ensemble, c'est la porte d'entrée qui est fermée.
 */

/** Au-delà, un échec n'est plus contemporain de ceux qui suivent. */
export const FENETRE_MS = 30_000
const SEUIL_ECHECS = 3
const SEUIL_ENDPOINTS = 2

let echecs = []
let panneAnnoncee = false

/** Segment d'URL qui identifie une ressource plutôt qu'une route. */
function estIdentifiant(segment) {
  return (
    /^\d+$/.test(segment) ||                       // z/x/y des tuiles, index
    /^\d+\.\w+$/.test(segment) ||                  // « 6011.pbf »
    /^[0-9a-f-]{16,}$/i.test(segment) ||           // UUID
    /^\d{5}/.test(segment) ||                      // code INSEE, id cadastral
    /\d/.test(segment) === true && segment.length >= 8 // références mêlant lettres et chiffres
  )
}

/**
 * Réduit une URL à la route qu'elle désigne.
 *
 * Sans cette normalisation, vingt tuiles refusées d'un même déplacement de
 * carte compteraient pour vingt endpoints distincts et déclencheraient à tort
 * le verdict « API à terre » — alors qu'une seule couche est en cause.
 */
export function cleEndpoint(url) {
  if (!url) return 'inconnu'
  const chemin = String(url).split('?')[0]
  return (
    chemin
      .split('/')
      .map((segment) => (estIdentifiant(segment) ? ':id' : segment))
      .join('/') || 'inconnu'
  )
}

export function signalerEchec(cle, now = Date.now()) {
  echecs = echecs.filter((e) => now - e.at < FENETRE_MS)
  echecs.push({ cle, at: now })
}

/**
 * Une seule réponse correcte suffit à prouver que l'API répond : on repart de
 * zéro plutôt que de laisser traîner un verdict périmé.
 */
export function signalerSucces() {
  if (echecs.length > 0) echecs = []
  panneAnnoncee = false
}

export function apiSembleHorsService(now = Date.now()) {
  const recents = echecs.filter((e) => now - e.at < FENETRE_MS)
  if (recents.length < SEUIL_ECHECS) return false
  return new Set(recents.map((e) => e.cle)).size >= SEUIL_ENDPOINTS
}

/**
 * Vrai une seule fois par épisode de panne.
 *
 * L'avertissement se rattache à l'épisode, pas à une minuterie : pendant une
 * panne qui dure, les endpoints tombent les uns après les autres et chacun
 * poserait son propre bandeau. Le compteur se réarme au premier succès, donc
 * une rechute après reprise avertit de nouveau — c'est bien une autre panne.
 */
export function doitAnnoncerPanne(now = Date.now()) {
  if (panneAnnoncee) return false
  if (!apiSembleHorsService(now)) return false
  panneAnnoncee = true
  return true
}

/** Réservé aux tests. */
export function reinitialiserSanteApi() {
  echecs = []
  panneAnnoncee = false
}
